import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { X, PhoneIncoming, Loader2, CheckCircle2 } from "lucide-react";
import { API } from "../constants";

const ZEITFENSTER = ["Mo–Fr, 8–12 Uhr", "Mo–Fr, 12–16 Uhr", "Mo–Fr, 16–19 Uhr", "Samstag vormittags"];

const EMPTY = { name: "", telefon: "", zeitraum: "", nachricht: "", datenschutz: false, website: "" };

const inputCls =
  "w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm text-ink placeholder:text-gray-400 focus:border-brand focus:ring-2 focus:ring-brand/20 focus:outline-none transition-colors duration-200";

export const CallbackModal = ({ open, onClose }) => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const close = () => {
    onClose();
    setTimeout(() => {
      setSent(false);
      setForm(EMPTY);
    }, 300);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/callback`, form);
      setSent(true);
    } catch (err) {
      toast.error("Ihr Rückrufwunsch konnte leider nicht gesendet werden. Bitte versuchen Sie es erneut oder rufen Sie uns direkt an.");
    } finally {
      setSending(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-navy/70 backdrop-blur-sm p-4"
          onClick={close}
          data-testid="callback-modal-overlay"
        >
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 40 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-6 sm:p-8 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Rückruf anfordern"
            data-testid="callback-modal"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-3">
                <span className="w-11 h-11 rounded-full bg-ice flex items-center justify-center shrink-0">
                  <PhoneIncoming className="w-5 h-5 text-brand" />
                </span>
                <div>
                  <h3 className="font-heading text-xl font-semibold text-ink">Wir rufen Sie zurück</h3>
                  <p className="text-xs text-mutedtext mt-0.5">Kostenlos &amp; unverbindlich</p>
                </div>
              </div>
              <button onClick={close} aria-label="Schließen" className="text-gray-400 hover:text-ink transition-colors" data-testid="callback-modal-close">
                <X className="w-5 h-5" />
              </button>
            </div>

            {sent ? (
              <div className="py-8 text-left" data-testid="callback-success">
                <CheckCircle2 className="w-12 h-12 text-conv" strokeWidth={1.5} />
                <h4 className="font-heading text-xl font-semibold text-ink mt-4">Rückrufwunsch erhalten!</h4>
                <p className="mt-2 text-sm text-mutedtext leading-relaxed">
                  Vielen Dank! Wir rufen Sie im gewünschten Zeitraum zurück.
                </p>
                <button
                  onClick={close}
                  className="mt-6 rounded-full bg-navy text-white text-sm font-semibold px-6 py-3"
                  data-testid="callback-success-close"
                >
                  Schließen
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-6 space-y-4" data-testid="callback-form">
                <div>
                  <label htmlFor="cb-name" className="block text-sm font-medium text-ink mb-1.5">
                    Name <span className="text-brand">*</span>
                  </label>
                  <input id="cb-name" required value={form.name} onChange={set("name")} className={inputCls} placeholder="Ihr Name" data-testid="callback-input-name" />
                </div>
                <div>
                  <label htmlFor="cb-tel" className="block text-sm font-medium text-ink mb-1.5">
                    Telefonnummer <span className="text-brand">*</span>
                  </label>
                  <input id="cb-tel" type="tel" required value={form.telefon} onChange={set("telefon")} className={inputCls} placeholder="0511 / 123456" data-testid="callback-input-telefon" />
                </div>
                <div>
                  <label htmlFor="cb-zeit" className="block text-sm font-medium text-ink mb-1.5">
                    Wunschzeitraum <span className="text-brand">*</span>
                  </label>
                  <select id="cb-zeit" required value={form.zeitraum} onChange={set("zeitraum")} className={inputCls} data-testid="callback-select-zeitraum">
                    <option value="" disabled>Bitte wählen</option>
                    {ZEITFENSTER.map((z) => (
                      <option key={z} value={z}>{z}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="cb-nachricht" className="block text-sm font-medium text-ink mb-1.5">Nachricht</label>
                  <textarea id="cb-nachricht" rows={2} value={form.nachricht} onChange={set("nachricht")} className={inputCls} placeholder="Worum geht es? (optional)" data-testid="callback-textarea-nachricht" />
                </div>
                <div className="hidden" aria-hidden="true">
                  <label htmlFor="cb-website">Website</label>
                  <input id="cb-website" tabIndex="-1" autoComplete="off" value={form.website} onChange={set("website")} />
                </div>
                <label className="flex items-start gap-3 cursor-pointer" data-testid="callback-consent-label">
                  <input type="checkbox" required checked={form.datenschutz} onChange={set("datenschutz")} className="mt-1 w-4 h-4 shrink-0" data-testid="callback-checkbox-datenschutz" />
                  <span className="text-xs text-mutedtext leading-relaxed">
                    Ich habe die{" "}
                    <a href="/datenschutz" target="_blank" className="text-brand underline underline-offset-2">Datenschutzerklärung</a>{" "}
                    gelesen und stimme der Verarbeitung meiner Angaben zur Bearbeitung meines Rückrufwunschs zu. <span className="text-brand">*</span>
                  </span>
                </label>
                <button
                  type="submit"
                  disabled={sending}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-conv hover:bg-conv-dark disabled:opacity-60 text-white font-semibold px-6 py-3.5 text-sm transition-colors duration-200"
                  data-testid="callback-submit-button"
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <PhoneIncoming className="w-4 h-4" />}
                  {sending ? "Wird gesendet …" : "Rückruf anfordern"}
                </button>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
